import type OpenAI from 'openai'
import type { z } from 'zod'

import { AI_CHATBOT_CONFIG, type AiChatBotDebugLevel } from './config'
import type { BotCapabilityKey } from './contracts'
import type { ResolvedPageContext } from './pipeline/page-context'
import type { SavedFilterStructure } from '@/Atomic/Organisms/FilterBuilder/types'

export type { AiChatBotDebugLevel }

/**
 * A PISA entity class name, as an opaque string. The host-neutral core never
 * enumerates the concrete classes — the connector defines them (in the CRM, via
 * @/Data/const.DataClassName) and supplies them through the injected registry.
 * A plain string (not a branded type) so the connector's DataClassName enum
 * values assign without a cast.
 */
export type EntityClassName = string

/**
 * Schema attribute value kinds — host-neutral mirror of @/Data/typesV2.AttributeType.
 * Kept in the core so the engine's cached-schema shape carries no @/Data dependency;
 * the Scrivito connector emits these same string literals.
 */
export type AttributeType =
  | 'string'
  | 'number'
  | 'enum'
  | 'enumarray'
  | 'date'
  | 'datetime' // alias for 'date'
  | 'reference'
  | 'boolean'

/**
 * Reference-population rule carried through the cached schema — host-neutral mirror of
 * @/Data/typesV2.ReferenceFilter. Used to derive the concrete target DataClass for a
 * reference field (e.g. Contact + isExternal → Company) during name→_id resolution.
 */
export type ReferenceFilter = {
  isExternal: boolean
  isPerson: boolean
}

// ─── Entity registry ────────────────────────────────────────────────────────
// Host-neutral shape of the per-host entity registry. The VALUE (the concrete
// NeoCRM registry) lives in the connector (connector/registry.ts) and is injected
// through HostRuntime; the core reads it via getEntityRegistry(). Keeping the
// TYPES here lets the generic tools stay entity-vocabulary-free.

export type JunctionSpec = {
  junctionClass: EntityClassName
  parentFilterKey: string
  parentFilterExtra?: Record<string, EntityClassName>
  idField: string
}

export type RelationSpec = {
  dataClass: EntityClassName
  filterKey?: string
  viaJunction?: JunctionSpec
  summaryLimit?: number
  defaultLimit?: number
  defaultOrder?: Array<[string, 'asc' | 'desc']>
}

export type WriteConfig = {
  create: boolean
  update: boolean
}

/**
 * Cross-field write constraints that the boolean `mandatory` schema flag cannot express.
 * `requireTogether`: the listed fields are conditionally-required as a set — each is
 * optional on its own, but supplying any one makes the others mandatory (e.g. Activity
 * beginAt/endAt). A sanctioned schema exemption; get_write_schema surfaces it so the
 * fields are presented as "optional — set all or none" without being marked required.
 */
export type FieldGroup = {
  requireTogether: string[]
}

export type EntityMetadata = {
  aliases: string[]
  searchable: boolean
  defaultOrder: Array<[string, 'asc' | 'desc']>
  userScopeFields?: string[]
  userScopeDefaults?: Record<string, unknown>
  relations: Record<string, RelationSpec>
  fetchHints?: {
    filterKeys?: string[]
    note?: string
  }
  /** Write-ops policy — undefined means no bot writes allowed. */
  write?: WriteConfig
  /** Cross-field write constraints not expressible via the schema's boolean `mandatory` flag. */
  fieldGroups?: FieldGroup[]
  /**
   * Field values set deterministically at create, bypassing the model. create_entity
   * injects these into the intent params before field-mapping, so a mandatory + readonly
   * field the model would otherwise be prompted for (e.g. List `type`) is fixed and can't
   * be hallucinated into the wrong value. get_write_schema omits these keys from the
   * prompted field list on create. The value `FORCE_CURRENT_USER` resolves to the current
   * user id at run time (for owner fields like List `responsible`).
   */
  forceAtCreate?: Record<string, string>
  /** Capability key required for this entity to be available on an instance. Absent = always available. */
  requiredFlag?: BotCapabilityKey
}

export type EntityRegistry = Partial<Record<EntityClassName, EntityMetadata>>

export const DEFAULT_MODEL = AI_CHATBOT_CONFIG.models.synthesize

// ─── Pipeline ─────────────────────────────────────────────────────────────────

export interface PipelineStage {
  model: string
  systemPrompt?: string
  tools?: AiChatBotTool<unknown>[]
  maxTokens?: number
}

export interface PipelineConfig {
  guard: PipelineStage
  gather: PipelineStage
  synthesize: PipelineStage
}

// ─── Config ───────────────────────────────────────────────────────────────────

export interface AiChatbotConfig {
  systemPrompt: string
  model?: string
  temperature?: number
  initialMessages?: OpenAI.ChatCompletionMessageParam[]
  initialDisplayMessage?: string
  tools?: (LegacyToolDefinition<unknown> | AiChatBotTool<unknown>)[]
  debug?: AiChatBotDebugLevel
  pipeline?: PipelineConfig
}

// ─── Legacy tool shape (structural match for @justrelate/chatbot ToolDefinition) ─

export interface LegacyToolDefinition<T> {
  name: string
  description: string
  parameters: z.ZodType<T>
  explanation?: (args: T) => string
  run: (args: T) => Promise<unknown>
}

// ─── AiChatBotTool ────────────────────────────────────────────────────────────

export interface AiChatBotTool<T> {
  name: string
  description: string
  concurrencySafe: boolean
  cacheable: boolean
  toOpenAI: () => OpenAI.ChatCompletionTool
  run: (args: T, toolCallId?: string) => Promise<unknown>
}

// ─── Tool execution ───────────────────────────────────────────────────────────

export interface ToolResult {
  tool_call_id: string
  content: string
}

export interface ActiveToolCall {
  id: string
  name: string
}

// ─── Conversation cache ───────────────────────────────────────────────────────

export interface MessageUsage {
  model: string
  inputTokens: number
  outputTokens: number
  // Estimated real token count for cached input, converted from gateway-reported credits
  // via creditsToTokens(). Non-zero from turn 2 onward when prompt caching is active.
  cachedTokens?: number
}

export type StoredMessage = OpenAI.ChatCompletionMessageParam & {
  usage?: MessageUsage
}

export interface ConversationEntry {
  messages: StoredMessage[]
  createdAt: number
  updatedAt: number
  preview: string
  error?: boolean
}

// ─── Conversation state ───────────────────────────────────────────────────────

/**
 * Industry-standard select-widget option shape (HTML <select>, MUI Autocomplete,
 * React Select, OpenAI function-calling examples). The model emits these from
 * search results; the user picks by `value`; downstream tools receive the value
 * (typically an entity id) — never the display label.
 *
 * Section pickers ("Activities", "Tickets", ...) use plain strings which the
 * tool normalises to `{ value: x, label: x }` — no entity metadata, no slot write.
 */
export type SelectOption = {
  value: string
  label: string
  dataClass?: EntityClassName
  entityId?: string
  url?: string
}

export type ShowOptionsResult =
  | { selected: string[]; cancelled: boolean }
  | { startDate: string; endDate: string; cancelled: boolean }

export type PendingToolCall =
  | {
      type: 'options'
      toolCallId: string
      question: string
      options: SelectOption[]
      multiSelect?: boolean
      preselected?: string[]
      // When true, the user may submit with nothing selected (opt-in refinement,
      // e.g. the summarize section picker where "none" means base info only).
      // Default/undefined keeps the "must pick at least one" gate for every other
      // options flow (entity confirmation, team-leader / account-director pickers).
      allowEmpty?: boolean
    }
  | {
      type: 'dateRange'
      toolCallId: string
      question: string
      defaultStartDate?: string
      defaultEndDate?: string
    }
  | {
      type: 'confirm'
      toolCallId: string
      spec: PreviewCardSpec
    }
  | {
      type: 'filterPreview'
      toolCallId: string
      entityType: string
      structure: SavedFilterStructure
      matchCount: number | null
      description: string
      fieldLabels?: Record<string, string>
      enumLabels?: Record<string, Record<string, string>>
      /**
       * What the primary button does. 'apply' (default) navigates to the filtered
       * list; 'save' resolves without navigating so the caller can persist the
       * filter (e.g. onto a segment). Keeps the card segment-agnostic.
       */
      primaryAction?: 'apply' | 'save'
      /** i18n key for the primary button label (defaults to the Apply label). */
      primaryLabel?: string
      /**
       * Present only for the create_segment flow: renders the card as a segment
       * confirmation (segment name header + "Create segment" primary). Absent for
       * plain filter apply/refine previews, so those are untouched.
       */
      segment?: { name: string }
    }

export interface ConversationState {
  messages: OpenAI.ChatCompletionMessageParam[]
  isProcessing: boolean
  isThinking: boolean
  activeToolCalls: ActiveToolCall[]
  pendingToolCalls: PendingToolCall[]
  hasError?: boolean
  compactionDisabled?: boolean
}

// ─── Cost tracking ────────────────────────────────────────────────────────────

export type TurnStage =
  | 'guard'
  | 'gather'
  | 'synthesize'
  | 'compact'
  | 'classifier'

export interface TurnCost {
  stage: TurnStage
  model: string
  // Credits as reported by the JR GenAI gateway.
  // Cost formula: (inputCredits + outputCredits) * 0.0000165 = USD
  inputCredits: number
  outputCredits: number
  cacheReadInputCredits: number
  cacheCreationInputCredits: number
  durationMs: number
}

export interface SessionCosts {
  turns: TurnCost[]
  totalInputCredits: number
  totalOutputCredits: number
  totalCacheReadInputCredits: number
  totalCacheCreationInputCredits: number
  byModel: Record<string, { input: number; output: number }>
  perModelUsage: Record<string, TurnCost>
}

// ─── Session context ──────────────────────────────────────────────────────────

/**
 * Cached schema shape — the entity-invariant portion of get_schema's result.
 * Excludes per-call fields like _summarySections / _instruction which depend on entityId.
 */
export interface CachedSchemaAttribute {
  name: string
  title: string
  type: AttributeType
  mandatory: boolean
  creatable: boolean
  updatable: boolean
  values?: Array<{
    value: string
    label: string
    /** Co-dependency: this option is valid only when a controlling field (e.g. group) has one of these values. */
    constraints?: { [controllingKey: string]: string[] }
  }>
  targetClass?: string
  /**
   * For reference fields: the schema's referenceFilter, carried through so the
   * concrete target DataClass (e.g. Contact + isExternal → Company) is derivable
   * via getReferenceDataClass for name→_id resolution on both the filter and write
   * paths. Dropping it leaves an ambiguous `Contact` with no rule to resolve.
   */
  referenceFilter?: ReferenceFilter
}

/**
 * A filterable m:n / collection relation read from the entity schema's own
 * `relations` list — the same source the manual FilterBuilder consumes. Filtered
 * as `<relationPath>.id in [<ids>]`, mirroring useFilterBuilder.toSavedFormat.
 */
export interface CachedSchemaRelation {
  /** Target entity name as PISA reports it (e.g. "company", "activity"). */
  entityName: string
  /** Path used in the saved-filter field, e.g. "companies" → "companies.id". */
  relationPath: string
  relationType: 'many-to-many' | 'one-to-many' | 'many-to-one'
  /** Localized relation title, used for readable filter labels. */
  title?: string
}

export interface CachedSchema {
  dataClass: EntityClassName
  /** Visible attributes only (schema group `attributeOrder`) — what the model sees via get_schema. */
  attributes: CachedSchemaAttribute[]
  /**
   * Every attribute key PISA knows, including ungrouped/system fields
   * (createdAt, number, …). Used to serialize full record data — the prompt
   * renders some ungrouped fields (e.g. Messages/Documents createdAt), so
   * serialization must not be limited to the visible set.
   */
  allAttributeNames: string[]
  /**
   * Attribute keys to include when serializing a record for the model — every
   * key except readonly booleans. A readonly boolean is a computed/system flag
   * (e.g. Opportunity `open`) that mirrors another field; feeding it to the
   * model invites it to cross-check and narrate discrepancies against the field
   * it duplicates. Schema-derived (readonly + boolean), so it needs no per-field
   * or per-entity configuration.
   */
  serializableAttributeNames: string[]
  relations: Record<string, unknown>
  filterableRelations: CachedSchemaRelation[]
  defaultOrder: Array<[string, 'asc' | 'desc']>
  userScopeFields: string[]
  fetchHints?: { filterKeys?: string[]; note?: string }
}

export interface SessionContext {
  currentUserId: string
  schemas: Map<EntityClassName, CachedSchema>
  /** Resolution cache keyed on `${entityType ?? 'global'}:${query.toLowerCase()}`. Page-session lifetime. */
  resolveCache: Map<string, ResolvedEntity[]>
  /** BotCapabilityKey strings for enabled capabilities — read from HostBindings.capabilities. */
  featureFlags: Set<string>
  fetchedAt: number
  /**
   * Per-conversation slot writer. Bound by Conversation.runToolDirect before
   * passing ctx to the tool. Tools that resolve user-confirmed entity selections
   * (today: show_options single-select with entity metadata) call this to
   * populate the confirmedEntities slot list. Optional because the singleton
   * SessionContext returned by getSessionContext() doesn't carry it; only the
   * per-call wrapper does.
   */
  addConfirmedEntity?: (entity: ConfirmedEntity) => void
  /** Registers a pending show_options/navigate_to resolver on the Conversation instance. */
  registerToolCallResolver?: (
    toolCallId: string,
    pendingCall: PendingToolCall,
    resolve: (result: unknown) => void,
    reject: (reason: Error) => void,
  ) => void
  /** Resolves a previously registered tool call resolver on the Conversation instance. */
  resolveToolCallResolver?: (toolCallId: string, result: unknown) => void
  /** Live page context snapshot — populated by pipeline before each tool run. */
  pageContext?: ResolvedPageContext
}

// ─── Entity resolution ───────────────────────────────────────────────────────

export interface ResolvedEntity {
  id: string
  title: string
  entityType: EntityClassName
  confidence: 'exact' | 'ambiguous'
  url?: string
}

// ─── Confirmed entities (per-conversation slot system) ───────────────────────

/**
 * A confirmed entity slot — populated when the user explicitly resolves a name
 * via show_options (single-select) and the resolver writes the structured ref.
 *
 * Lives on the Conversation instance (not SessionContext) — slots are per-conversation,
 * cleared on conversation reset, never auto-expired by TTL, never shared across widgets.
 *
 * The disambiguation rule encoded in the system prompt:
 *   - Pronoun ("this", "it", "them", "there") → most recent slot of matching type
 *   - Bare type ("the company", "the deal") → same as pronoun
 *   - Named explicitly ("ARLAG", "Maria Schmidt") → always re-search via search_entities
 */
export interface ConfirmedEntity {
  dataClass: EntityClassName
  id: string
  title: string
  url: string
}

// ─── Write operations — card specs ───────────────────────────────────────────

export interface PreviewCardSpec {
  title: string
  rows: {
    label: string
    value: string
    /** 'user' = typed by user; 'resolved' = looked up by the engine (reference, enum lookup). Card renderer rejects mount if any row is missing this. */
    source: 'user' | 'resolved'
  }[]
}

/** A single missing required field with enough metadata for the model to ask a well-formed question. */
export interface MissingFieldInfo {
  /** PISA canonical key (e.g. "ticketType") */
  key: string
  /** Localized field label from the schema (e.g. "Type") */
  label: string
  /** For enum/enumarray fields: the allowed localized options. Undefined for free-text fields. */
  options?: Array<{ value: string; title: string }>
  /** 'enum' | 'enumarray' | 'string' | 'date' | 'reference' | ... */
  type: string
}

/**
 * A recognised enum field whose supplied value matched no option (neither code nor
 * localized title). Distinct from `invalid` (unrecognised field): the field is valid,
 * only the value is wrong. Carries the allowed options so the model can re-ask with a
 * human-readable list — the raw supplied value (often a `PSA_*` code the model guessed)
 * is NEVER written or shown.
 */
export interface InvalidValueInfo {
  /** PISA canonical key (e.g. "status") */
  key: string
  /** Localized field label from the schema (e.g. "Status") */
  label: string
  /** The value the user/model supplied that matched no option. */
  suppliedValue: string
  /** The allowed localized options for this field. */
  options: Array<{ value: string; title: string }>
}

/**
 * Returned by create_entity / update_entity when required fields are missing or
 * a reference is ambiguous. The model reads this and asks in chat — no form widget.
 */
export interface WriteNeedsInput {
  needs_input: {
    missing: MissingFieldInfo[]
    /** Fields the user supplied that did not match any schema key or localized title. */
    unrecognized?: Array<{ suppliedKey: string; suppliedValue: string }>
    /** Recognised enum fields whose supplied value matched no option — includes the allowed options. */
    invalidValues?: InvalidValueInfo[]
    ambiguous?: Array<{
      field: string
      query: string
      candidates: { id: string; title: string }[]
    }>
  }
}

/** Result of mapping user-supplied field values against the PISA schema. */
export type FieldMapping =
  | {
      ok: true
      /** Fields with resolved PISA values (enum codes, canonical keys). Used for the write. */
      mapped: Record<string, unknown>
      /** Human-readable display values for the confirmation card (same keys as mapped). */
      displayMapped: Record<string, string>
      /** Localized field labels from the schema (canonical key → localized title). Used for card row labels. */
      labelMap: Record<string, string>
      /** For enum fields: raw PISA value code → localized title. Used to display current values in update diffs. */
      enumDisplayMap: Record<string, Record<string, string>>
      /** Required fields not supplied by the user, with schema metadata for the model to form a question. */
      missing: MissingFieldInfo[]
      /** Field names supplied but not recognised by the schema or not in the editable whitelist. */
      invalid: string[]
      /** Update-only: recognised fields that are readonly after creation (e.g. Activity `type`) — rejected, but as "can't be changed" rather than "not recognised". */
      immutable: Array<{ key: string; label: string }>

      /** Recognised enum fields whose supplied value matched no option — the value is rejected, never written. */
      invalidValues: InvalidValueInfo[]
      /** For reference fields being changed on update: the current value's resolved display name (by id), so the diff card reads "Name → Name" instead of leaking a raw id. */
      currentDisplayMap: Record<string, string>
      /** Reference fields whose supplied _id resolves to no live record (a stale or guessed id) — rejected, never written, to avoid persisting a dangling reference. */
      unresolvedReferences: Array<{ key: string; label: string; value: string }>
    }
  | {
      ok: false
      error:
        | 'schema-unavailable'
        | 'invalid-entity-type'
        | 'invalid-datetime-range'
      message: string
    }

/**
 * Discriminated union for tool result content.
 * - 'text': plain text / markdown (existing behaviour)
 */
export type ToolResultContent = { kind: 'text'; content: string }
